import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { ReelBand } from "@/components/ReelBand";
import { HumanPlusAI } from "@/components/HumanPlusAI";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { IMG, VIDEO, POSTER } from "@/lib/images";

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Eyebrow = ({ children, dark }) => (
  <span
    className={`text-[12px] font-semibold uppercase tracking-[0.18em] ${
      dark ? "text-[var(--mint)]" : "text-[var(--ember)]"
    }`}
  >
    {children}
  </span>
);

// clamp helpers for the display type system
const h1Style = { fontSize: "clamp(44px, 8.5vw, 104px)", letterSpacing: "-0.03em", lineHeight: 1.03 };
const headStyle = { fontSize: "clamp(38px, 6.5vw, 80px)", letterSpacing: "-0.025em", lineHeight: 1.04 };
const bigStyle = { fontSize: "clamp(46px, 7.5vw, 92px)", letterSpacing: "-0.03em", lineHeight: 1.0 };

// ---- Full-bleed background video (in-view autoplay, pause offscreen) --------
const VideoBg = ({ video, poster }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Ensure muted is set on the DOM element itself — React's `muted` prop is
    // not reliably applied, which blocks autoplay on iOS/Android.
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    if (prefersReduced()) {
      v.removeAttribute("autoplay");
      v.pause();
      return;
    }
    const tryPlay = () => v.play().catch(() => {});
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? tryPlay() : v.pause()),
      { threshold: 0.2 },
    );
    io.observe(v);
    v.addEventListener("loadeddata", tryPlay);
    return () => {
      io.disconnect();
      v.removeEventListener("loadeddata", tryPlay);
    };
  }, []);

  return (
    <video
      ref={ref}
      src={video}
      poster={poster}
      muted
      loop
      autoPlay
      playsInline
      preload="metadata"
      aria-hidden="true"
      className="absolute inset-0 h-full w-full object-cover"
    />
  );
};

const VideoPanel = ({ video, poster, id, testid, children, minH = "min-h-[100svh]" }) => (
  <section
    id={id}
    data-testid={testid}
    className={`relative flex ${minH} w-full items-center overflow-hidden snap-panel`}
  >
    <VideoBg video={video} poster={poster} />
    <div className="absolute inset-0 bg-gradient-to-r from-[#141210]/90 via-[#141210]/50 to-[#141210]/10" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#141210]/40 via-transparent to-transparent" />
    <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12">{children}</div>
  </section>
);

// ---- Animated flow connector (How it works) --------------------------------
const FlowConnector = ({ index = 0 }) => (
  <div className="relative my-2 h-9 w-px bg-black/10" aria-hidden="true">
    <span
      className="flow-dot absolute left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-[var(--ember)] shadow-[0_0_10px_2px_rgba(0,245,160,0.35)]"
      style={{ animationDelay: `${index * 0.8}s` }}
    />
  </div>
);

// ---- Static data ------------------------------------------------------------
const trustLogos = ["Nissan", "McCormick & Company", "NIKE", "Pepsi", "Facebook", "Fox Sports", "Prime Real Estate", "Newhall Escrow", "Puma", "Keys & More", "Caesars Entertainment", "Tejon Ranch"];

const OPERATORS = [
  { name: "Tamer Abdel", role: "Founder & CEO", photo: `${process.env.PUBLIC_URL}/team/tamer-abdel.jpg` },
  { name: "Daniyal Syed", role: "Growth Team lead", photo: `${process.env.PUBLIC_URL}/team/daniyal-syed.jpg` },
  { name: "Muhammad Daud", role: "Product & Engineering lead", photo: `${process.env.PUBLIC_URL}/team/muhammad-daud.jpg` },
  { name: "Nabab Uddin", role: "Creative lead", photo: "https://customer-assets.emergentagent.com/job_human-layer-demo/artifacts/86ey59l3_IMG_7184.jpg" },
];

const FAQ = [
  { q: "Is this software we manage?", a: "No. It's done-for-you. We build, run, and answer for the AI. You read the results." },
  { q: "What does it cost?", a: "It depends on scope. The free audit comes first, so the recommendation ties to a real win." },
  { q: "Do we replace our tools?", a: "Usually not. We install agents around the tools you already use and improve the handoffs." },
  { q: "How fast can we launch?", a: "The main path is 30 days: audit, design, build, and phased launch." },
  { q: "What if the AI makes a mistake?", a: "Sensitive actions pause for human approval. Scopes, logs, and escalation are set up front." },
  { q: "Can customers tell it's AI?", a: "Only if you want them to. Many installs start with human-approved drafts, then expand." },
];

// ---- Page -------------------------------------------------------------------
export default function Home() {
  const [phase, setPhase] = useState(null); // eslint-disable-line no-unused-vars

  useEffect(() => {
    document.documentElement.classList.add("snap-home");
    return () => document.documentElement.classList.remove("snap-home");
  }, []);

  return (
    <>
      {/* 1. Hero */}
      <VideoPanel video={VIDEO.unfair} poster={POSTER.unfair} testid="home-hero" minH="min-h-[100svh]">
        <div className="max-w-4xl">
          <Reveal delay={100}>
            <h1 style={h1Style} className="mt-6 font-semibold text-white">
              Add an AI department into your business.
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-8 text-lg text-white/80 sm:text-xl">
              A full team of AI agents and real experts that plugs into your business — installed, managed, and reporting to you.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button to="/audit" variant="primary" data-testid="hero-cta">
                Install your department
              </Button>
              <Button to="/contact" variant="ghostDark" data-testid="hero-cta-secondary">
                Talk to us
              </Button>
            </div>
            <p className="mt-4 text-sm text-white/55">
              Free Business DNA audit. Six scores. Not a pitch.
            </p>
          </Reveal>
        </div>
      </VideoPanel>

      {/* 1a. Trust bar — dark scrolling logo band (directly under hero) */}
      <section className="bg-[var(--dark)] py-10 text-[var(--dark-text)]" data-testid="trust-bar">
        <p className="mb-7 text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-white/45">
          Trusted by businesses moving fast
        </p>
        <div
          className="relative flex overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div className="marquee-track flex flex-shrink-0 items-center gap-16 pr-16">
            {trustLogos.concat(trustLogos, trustLogos).map((l, i) => (
              <span
                key={`${l}-${i}`}
                className="flex-shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight text-white/55 transition-colors duration-300 hover:text-white sm:text-2xl"
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 1b. Real experts + AI bento */}
      <HumanPlusAI />

      {/* 1c. Why this matters now — full-bleed video beat */}
      <VideoPanel video={VIDEO.competitors} poster={POSTER.competitors} testid="home-why-now">
        <div className="max-w-4xl">
          <Reveal><Eyebrow dark>Why this matters now</Eyebrow></Reveal>
          <Reveal delay={100}>
            <h2 style={headStyle} className="mt-7 font-semibold text-white">
              Your competitors are automating while you're still doing it by hand.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-5 max-w-2xl text-lg text-white/70">
              Speed decides who wins the customer — and most businesses are losing on the clock.
            </p>
          </Reveal>
        </div>
      </VideoPanel>

      {/* 3. Problem */}
      <VideoPanel video={VIDEO.engineering} poster={POSTER.engineering} testid="home-problem">
        <div className="max-w-4xl">
          <Reveal><Eyebrow dark>The problem</Eyebrow></Reveal>
          <Reveal delay={100}>
            <h2 style={headStyle} className="mt-7 font-semibold text-white">
              Doing it all <br />yourself is the problem.
            </h2>
          </Reveal>
          <div className="mt-10 flex flex-col gap-4">
            <Reveal delay={160}>
              <p className="text-xl font-semibold text-white/90 sm:text-2xl">
                Everyone's an AI expert. Almost no one is.
              </p>
            </Reveal>
          </div>
          <Reveal delay={320}>
            <p className="mt-8 text-lg text-white/70">
              AI is too deep to master and too fragmented to hire.
            </p>
          </Reveal>
        </div>
      </VideoPanel>

      {/* 4. Unfair advantage */}
      <VideoPanel video={VIDEO.home} poster={POSTER.home} testid="home-unfair">
        <div className="max-w-4xl">
          <Reveal><Eyebrow dark>The escape</Eyebrow></Reveal>
          <Reveal delay={100}>
            <h2 style={h1Style} className="mt-6 font-semibold text-white">
              We are your <br />unfair advantage.
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 text-lg text-white/80 sm:text-xl">
              One department. Every specialist. Already coordinated.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-9">
              <Button href="/workflow" variant="ghostDark" data-testid="unfair-cta">
                See how it works
              </Button>
            </div>
          </Reveal>
        </div>
      </VideoPanel>

      {/* 5. How it works — animated, wordless */}
      <section className="snap-panel flex min-h-[100svh] flex-col justify-center bg-[var(--bg-base)] py-28" data-testid="home-how">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Eyebrow>How it works</Eyebrow></Reveal>
          <Reveal delay={100}>
            <h2 style={headStyle} className="mt-7 max-w-4xl font-semibold text-[var(--ink)]">
              Two teams. One human layer. Zero handoffs.
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-4 text-lg text-[var(--muted)]">AI does the work. A human answers for it.</p>
          </Reveal>

          <div className="mx-auto mt-16 flex w-full max-w-3xl flex-col items-stretch">
            {/* AI Standards — the human layer */}
            <Reveal>
              <div className="rounded-2xl border-2 border-[var(--ember)]/45 bg-[var(--ember)]/[0.05] p-7">
                <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
                  AI Standards — the human layer
                </p>
                <p className="mt-3 text-[15px] leading-relaxed text-[var(--ink)]">
                  Your DNA audit, strategy, quality, and proof. The people who
                  direct the agents and answer for the work.
                </p>
              </div>
            </Reveal>

            <div className="flex justify-center"><FlowConnector index={0} /></div>

            {/* Two teams */}
            <Reveal delay={120}>
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  { t: "Marketing & Creative", s: "The front office. Content, campaigns, and AI agents that turn attention into customers — sales is the outcome." },
                  { t: "Workflow & Automation", s: "The back office. Optimization + automation that runs the business." },
                ].map((team) => (
                  <div
                    key={team.t}
                    className="rounded-2xl border border-black/10 bg-[var(--bg-contrast)] p-6 text-center transition-transform duration-300 hover:-translate-y-1"
                  >
                    <p className="text-base font-semibold tracking-tight text-[var(--ink)]">{team.t}</p>
                    <p className="mt-1.5 text-[13px] text-[var(--muted)]">{team.s}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <div className="flex justify-center"><FlowConnector index={1} /></div>

            {/* Proactive delivery */}
            <Reveal delay={240}>
              <div className="rounded-2xl border border-black/10 bg-[var(--bg-contrast)] p-7">
                <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--ink)]">
                  Proactive delivery
                </p>
                <p className="mt-3 text-[15px] leading-relaxed text-[var(--muted)]">
                  Reports, approvals, and wins arrive where you already are. You
                  log in to verify, not to stay informed.
                </p>
              </div>
            </Reveal>

            <div className="flex justify-center"><FlowConnector index={2} /></div>

            {/* You */}
            <Reveal delay={360}>
              <div className="rounded-2xl bg-[var(--dark)] py-6 text-center">
                <span className="text-sm font-semibold uppercase tracking-[0.28em] text-white">You</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6. AI Standards spotlight */}
      <VideoPanel video={VIDEO.standards} poster={POSTER.standards} testid="home-standards">
        <div className="max-w-4xl">
          <Reveal><Eyebrow dark>AI Standards</Eyebrow></Reveal>
          <div className="mt-14 flex flex-col gap-2">
            {["Quality.", "Compliance.", "Taste."].map((w, i) => (
              <Reveal key={w} delay={i * 160}>
                <p style={bigStyle} className="font-semibold text-white/90">{w}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={520}>
            <p className="mt-10 text-lg text-white/60">Nothing ships until a human clears it.</p>
          </Reveal>
        </div>
      </VideoPanel>

      {/* 7. Meet the operators — human faces */}
      <section className="snap-panel flex min-h-[100svh] flex-col justify-center bg-[var(--bg-base)] py-28" data-testid="home-operators">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Eyebrow>Meet the operators</Eyebrow></Reveal>
          <Reveal delay={100}>
            <h2 style={headStyle} className="mt-7 max-w-3xl font-semibold text-[var(--ink)]">
              Every agent has a human behind it.
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-4 text-lg text-[var(--muted)]">The one thing software can't copy.</p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {OPERATORS.map((op, i) => (
              <Reveal key={op.name} delay={i * 120}>
                <div className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#141210]">
                  <img
                    src={op.photo}
                    alt={op.name}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">{op.role}</p>
                    <p className="mt-1 text-xl font-semibold text-white">{op.name}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Closing CTA */}
      <VideoPanel video={VIDEO.closing} poster={POSTER.closing} id="closing" testid="home-closing">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal><Eyebrow dark>Get started</Eyebrow></Reveal>
          <Reveal delay={100}>
            <h2 style={h1Style} className="mt-6 font-semibold text-white">Your unfair advantage is one call away.</h2>
          </Reveal>
          <Reveal delay={220}>
            <p className="mx-auto mt-8 max-w-2xl text-lg text-white/80 sm:text-xl">Start with a free Business DNA audit — no pitch, just a clear read on where AI moves the needle.</p>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-9 flex justify-center">
              <Button to="/audit" variant="primary" data-testid="closing-cta">
                Get my free audit
              </Button>
            </div>
          </Reveal>
        </div>
      </VideoPanel>

      {/* --- Below the fold (demoted) --- */}

      {/* Reel feature band */}
      <ReelBand />

      {/* FAQ */}
      <section className="bg-[var(--bg-contrast)] py-20 md:py-28" data-testid="home-faq">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal><Eyebrow>Questions</Eyebrow></Reveal>
          <Reveal delay={100}>
            <h3 className="mt-6 t-sub text-[var(--ink)]">
              Clear answers before you start.
            </h3>
          </Reveal>
          <Reveal delay={160}>
            <Accordion type="single" collapsible className="mt-8">
              {FAQ.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border-black/10" data-testid={`home-faq-${i}`}>
                  <AccordionTrigger className="py-5 text-left text-lg font-semibold tracking-[-0.01em] text-[var(--ink)] hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[var(--muted)]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Closing CTA band — standard footer CTA */}
      <CtaBand />
    </>
  );
}
